import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash, Play, SlidersHorizontal, Trophy, DotsThreeVertical, ArrowsOut, Flag } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError, getWithRetry } from "../../lib/api";
import { useRealtime } from "../../hooks/useRealtime";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import MatchSetupDialog from "./MatchSetupDialog";
import MatchFullscreen from "./MatchFullscreen";
import ConfirmDialog from "../ConfirmDialog";

const STATUS = {
  scheduled: { label: "Programado", cls: "text-muted-foreground border-white/15" },
  live: { label: "En vivo", cls: "text-padel-green border-padel-green animate-pulse-glow" },
  finished: { label: "Finalizado", cls: "text-padel-blue border-padel-blue" },
};

function teamName(t) {
  return t.p2 ? `${t.p1} / ${t.p2}` : t.p1;
}

export default function MatchesPanel() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [courts, setCourts] = useState([]);
  const [open, setOpen] = useState(false);
  const [fsId, setFsId] = useState(null);
  const [courtFilter, setCourtFilter] = useState("all");
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => getWithRetry("/matches").then((r) => setMatches(r.data)).catch(() => {}), []);
  useEffect(() => { load(); getWithRetry("/courts").then((r) => setCourts(r.data)).catch(() => {}); }, [load]);

  useRealtime(useCallback((msg) => {
    if (msg.type === "refresh") { load(); api.get("/courts").then((r) => setCourts(r.data)); }
    else if (msg.type === "match_update" && msg.match) {
      setMatches((prev) => prev.map((m) => (m.id === msg.match.id ? msg.match : m)));
    }
  }, [load]));

  const remove = async (id) => { await api.delete(`/matches/${id}`); load(); toast.success("Partido eliminado"); };
  const finish = async (id) => {
    try { await api.post(`/matches/${id}/finish`); load(); toast.success("Partido finalizado"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const start = async (id) => {
    try { await api.post(`/matches/${id}/start`); navigate(`/control/${id}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sortedCourts = [...courts].sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }));
  const visibleMatches = courtFilter === "all" ? matches : matches.filter((m) => m.court_id === courtFilter);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="font-heading text-3xl font-extrabold uppercase">Partidos</h2>
        <div className="flex items-center gap-3">
          <Select value={courtFilter} onValueChange={setCourtFilter}>
            <SelectTrigger data-testid="court-filter" className="bg-[#131417] border-white/15 w-44 h-10 rounded-none font-heading uppercase tracking-widest text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las canchas</SelectItem>
              {sortedCourts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="new-match-btn" onClick={() => setOpen(true)} className="bg-padel-blue text-black font-heading uppercase tracking-widest rounded-none hover:bg-padel-green">
            <Plus size={18} className="mr-1" /> Nuevo partido
          </Button>
        </div>
      </div>

      <MatchSetupDialog open={open} onOpenChange={setOpen} onCreated={load} />

      {visibleMatches.length === 0 ? (
        <p className="text-muted-foreground">{matches.length === 0 ? "No hay partidos. Configura uno nuevo para empezar." : "No hay partidos en esta cancha."}</p>
      ) : (
        <div className="space-y-4">
          {visibleMatches.map((m) => {
            const st = STATUS[m.status] || STATUS.scheduled;
            const s = m.score;
            return (
              <div key={m.id} data-testid={`match-card-${m.id}`} className="bg-[#131417] border border-white/10 p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 border ${st.cls}`}>{st.label}</span>
                      <span className="text-xs tracking-widest uppercase text-muted-foreground">{m.court_name}</span>
                    </div>
                    <div className="space-y-2">
                      <div className={`flex items-center justify-between font-medium ${s.winner === 1 ? "text-padel-green" : ""}`}>
                        <span className="flex items-center gap-2 min-w-0">
                          {s.winner === 1 && <Trophy size={16} weight="fill" className="shrink-0" />}
                          <span className="flex flex-col leading-tight min-w-0">
                            <span className="truncate">{m.team1.p1}</span>
                            {m.team1.p2 && <span className="truncate">{m.team1.p2}</span>}
                          </span>
                        </span>
                        <span className="font-heading text-xl tabular-nums shrink-0 flex items-center">
                          <span className="w-6 text-center">{s.sets1}</span>
                          <span className="text-muted-foreground">·</span>
                          <span className="w-6 text-center">{s.games1}</span>
                          <span className="text-muted-foreground">·</span>
                          <span className="w-10 text-center">{s.points1}</span>
                        </span>
                      </div>
                      <div className="h-px bg-white/10" />
                      <div className={`flex items-center justify-between font-medium ${s.winner === 2 ? "text-padel-green" : ""}`}>
                        <span className="flex items-center gap-2 min-w-0">
                          {s.winner === 2 && <Trophy size={16} weight="fill" className="shrink-0" />}
                          <span className="flex flex-col leading-tight min-w-0">
                            <span className="truncate">{m.team2.p1}</span>
                            {m.team2.p2 && <span className="truncate">{m.team2.p2}</span>}
                          </span>
                        </span>
                        <span className="font-heading text-xl tabular-nums shrink-0 flex items-center">
                          <span className="w-6 text-center">{s.sets2}</span>
                          <span className="text-muted-foreground">·</span>
                          <span className="w-6 text-center">{s.games2}</span>
                          <span className="text-muted-foreground">·</span>
                          <span className="w-10 text-center">{s.points2}</span>
                        </span>
                      </div>
                    </div>
                    <p className="mt-2 text-[10px] tracking-widest uppercase text-muted-foreground flex items-center gap-1">
                      <SlidersHorizontal size={12} /> {m.config.sets_to_win} sets · {m.config.games_per_set} games · {m.config.golden_point ? "punto de oro" : "ventaja"}
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    {m.status !== "finished" && (
                      <Button data-testid={`control-match-${m.id}`} onClick={() => start(m.id)} className="bg-padel-green text-black font-heading uppercase tracking-widest rounded-none">
                        <Play size={18} weight="fill" className="mr-1" /> {m.status === "live" ? "Marcador" : "Iniciar"}
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`match-menu-${m.id}`} className="p-2 text-muted-foreground hover:text-white transition-colors">
                          <DotsThreeVertical size={24} weight="bold" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#131417] border-white/10 text-white">
                        <DropdownMenuItem data-testid={`fullscreen-match-${m.id}`} onClick={() => setFsId(m.id)} className="cursor-pointer focus:bg-white/10 focus:text-padel-blue">
                          <ArrowsOut size={18} className="mr-2" /> Pantalla completa
                        </DropdownMenuItem>
                        {m.status !== "finished" && (
                          <DropdownMenuItem data-testid={`finish-match-${m.id}`} onClick={() => finish(m.id)} className="cursor-pointer focus:bg-white/10 focus:text-padel-green">
                            <Flag size={18} className="mr-2" /> Terminar partido
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem data-testid={`delete-match-${m.id}`} onClick={() => setConfirm(m)} className="cursor-pointer text-padel-danger focus:bg-padel-danger/10 focus:text-padel-danger">
                          <Trash size={18} className="mr-2" /> Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {fsId && <MatchFullscreen match={matches.find((m) => m.id === fsId)} court={courts.find((c) => c.id === (matches.find((m) => m.id === fsId) || {}).court_id)} onClose={() => setFsId(null)} />}

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Eliminar partido"
        description={confirm ? `¿Seguro que quieres eliminar este partido (${teamName(confirm.team1)} vs ${teamName(confirm.team2)})? Esta acción no se puede deshacer.` : ""}
        onConfirm={() => confirm && remove(confirm.id)}
      />
    </div>
  );
}
